/*
 * Copyright (c) 2024-2026, s0up and the autobrr contributors.
 * SPDX-License-Identifier: GPL-2.0-or-later
 */

import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ArrowLeftIcon, PencilIcon, TrashIcon, PlusIcon, SignalSlashIcon } from "@heroicons/react/24/outline";
import { Button } from "@/components/ui/Button";
import { useMobileHeaderAction } from "@/context/tabNavigation";
import { PageHeader } from "@/components/common/PageHeader";
import { DeleteConfirmationDialog } from "@/components/common/DeleteConfirmationDialog";
import { showToast } from "@/components/common/Toast";
import { SwitchCards, SwitchSummary, SwitchTable, StatusPill, modelText, uptimeText, useSwitchInfo, useSwitchInfos } from "./SwitchViews";
import { EmptyState, SkeletonRows } from "@/components/common/States";
import { StatusDot } from "@/components/servers/kit";
import { useIsDesktop } from "@/hooks/useMediaQuery";
import { SNMPDeviceForm } from "./SNMPDeviceForm";
import { SNMPDeviceDetailsTabs } from "./SNMPDeviceDetailsTabs";
import { useFocus, useReportSelected } from "@/utils/sidebarFocus";
import {
  getSNMPDevices,
  createSNMPDevice,
  updateSNMPDevice,
  deleteSNMPDevice,
  SNMPDevice,
  CreateSNMPDeviceRequest,
  UpdateSNMPDeviceRequest,
} from "@/api/snmp";
import { SNMP_REFRESH_INTERVALS } from "@/constants/snmpRefreshIntervals";

const DEFAULT_POLL_INTERVAL_SECONDS = 30;

export const SNMPTab: React.FC = () => {
  const [selectedDevice, setSelectedDevice] = useState<SNMPDevice | null>(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingDevice, setEditingDevice] = useState<SNMPDevice | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deviceToDelete, setDeviceToDelete] = useState<SNMPDevice | null>(null);
  const queryClient = useQueryClient();

  const { data: devices = [], isLoading } = useQuery({
    queryKey: ["snmp-devices"],
    queryFn: getSNMPDevices,
    refetchInterval: SNMP_REFRESH_INTERVALS.DEVICES_LIST,
  });

  useReportSelected("switches", selectedDevice?.id);

  // Sidebar sub-item clicks select a specific switch.
  useFocus("switches", (id) => {
    const d = devices.find((x) => x.id === parseInt(id, 10));
    if (d) setSelectedDevice(d);
  });

  const createMutation = useMutation({
    mutationFn: createSNMPDevice,
    onSuccess: (newDevice) => {
      queryClient.invalidateQueries({ queryKey: ["snmp-devices"] });
      setIsFormOpen(false);
      showToast("Switch added", "success", {
        description: `${newDevice.name} has been added successfully`,
      });
    },
    onError: (error: Error) => {
      showToast("Failed to add switch", "error", {
        description: error.message || "Unable to create the SNMP device",
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateSNMPDeviceRequest }) =>
      updateSNMPDevice(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["snmp-devices"] });
      setIsFormOpen(false);
      setEditingDevice(null);
      showToast("Switch updated", "success", {
        description: "Switch has been updated successfully",
      });
    },
    onError: (error: Error) => {
      showToast("Failed to update switch", "error", {
        description: error.message || "Unable to update the SNMP device",
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteSNMPDevice,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["snmp-devices"] });
      setSelectedDevice(null);
      showToast("Switch deleted", "success", {
        description: `${deviceToDelete?.name || "Switch"} has been removed`,
      });
    },
    onError: (error: Error) => {
      showToast("Failed to delete switch", "error", {
        description: error.message || "Unable to delete the SNMP device",
      });
    },
  });

  const handleCreateDevice = () => {
    setEditingDevice(null);
    setIsFormOpen(true);
  };

  const handleEditDevice = (device: SNMPDevice) => {
    setEditingDevice(device);
    setIsFormOpen(true);
  };

  const handleDeleteDevice = (device: SNMPDevice) => {
    setDeviceToDelete(device);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = () => {
    if (deviceToDelete) {
      setDeleteDialogOpen(false);
      deleteMutation.mutate(deviceToDelete.id);
      setDeviceToDelete(null);
    }
  };

  const handleFormSubmit = (data: CreateSNMPDeviceRequest) => {
    if (editingDevice) {
      updateMutation.mutate({ id: editingDevice.id, data: { ...data, id: editingDevice.id } });
    } else {
      createMutation.mutate(data);
    }
  };

  // Mobile: surface "Add switch" in the top bar (device list view only).
  useMobileHeaderAction(
    () =>
      selectedDevice ? null : (
        <Button
          onClick={handleCreateDevice}
          variant="ghost"
          size="icon"
          className="h-8 w-8 text-gray-600 dark:text-gray-400"
          aria-label="Add switch"
        >
          <PlusIcon className="h-5 w-5" />
        </Button>
      ),
    [selectedDevice]
  );

  const desktop = useIsDesktop();
  const [view, setView] = useState<"table" | "cards">(() => {
    try {
      return localStorage.getItem("nyrvex-switches-view") === "cards" ? "cards" : "table";
    } catch {
      return "table";
    }
  });
  const changeView = (v: "table" | "cards") => {
    setView(v);
    try {
      localStorage.setItem("nyrvex-switches-view", v);
    } catch {
      /* ignore */
    }
  };
  const [filter, setFilter] = useState("");
  const infos = useSwitchInfos(devices);
  const q = filter.trim().toLowerCase();
  const shownDevices = q ? devices.filter((d) => `${d.name} ${d.host}`.toLowerCase().includes(q)) : devices;
  const shownInfos = q ? infos.filter((_, i) => `${devices[i].name} ${devices[i].host}`.toLowerCase().includes(q)) : infos;

  return (
    <div className="gh sv">
      {!selectedDevice ? (
        <>
          <PageHeader
            title="Switches"
            subtitle={`${devices.length} ${devices.length === 1 ? "device" : "devices"} · SNMP-polled switches and routers`}
            actions={
              <>
                <div className="cc-seg" role="tablist" aria-label="View">
                  {(["table", "cards"] as const).map((v) => (
                    <button
                      key={v}
                      type="button"
                      role="tab"
                      aria-selected={view === v}
                      className={`cc-seg-btn${view === v ? " cc-seg-btn--on" : ""}`}
                      onClick={() => changeView(v)}
                    >
                      {v === "table" ? "Table" : "Cards"}
                    </button>
                  ))}
                </div>
                <button className="gh-btn gh-btn--go" onClick={handleCreateDevice}>
                  <PlusIcon /> Add switch
                </button>
              </>
            }
          />

          {isLoading ? (
            <SkeletonRows rows={6} />
          ) : devices.length === 0 ? (
            <EmptyState
              icon={<SignalSlashIcon className="h-8 w-8" />}
              title="No switches configured"
              hint="Add a switch or router to start SNMP monitoring: its ports, traffic, errors and uptime."
              action={
                <button className="gh-btn gh-btn--go" onClick={handleCreateDevice}>
                  <PlusIcon /> Add switch
                </button>
              }
            />
          ) : (
            <>
              <SwitchSummary devices={devices} infos={infos} />
              {devices.length > 6 && (
                <div className="sv-toolbar">
                  <input className="sw-filter" placeholder="Filter switches…" value={filter} onChange={(e) => setFilter(e.target.value)} aria-label="Filter switches" />
                </div>
              )}
              {shownDevices.length === 0 ? (
                <div className="sw-empty">No switches match.</div>
              ) : view === "table" && desktop ? (
                <SwitchTable devices={shownDevices} infos={shownInfos} onSelect={setSelectedDevice} onEdit={handleEditDevice} onDelete={handleDeleteDevice} />
              ) : (
                <SwitchCards devices={shownDevices} infos={shownInfos} onSelect={setSelectedDevice} onEdit={handleEditDevice} onDelete={handleDeleteDevice} />
              )}
            </>
          )}
        </>
      ) : (
        <SwitchDetail
          key={selectedDevice.id}
          device={selectedDevice}
          onBack={() => setSelectedDevice(null)}
          onEdit={() => handleEditDevice(selectedDevice)}
          onDelete={() => handleDeleteDevice(selectedDevice)}
        />
      )}

      <SNMPDeviceForm
        device={editingDevice}
        onSubmit={handleFormSubmit}
        onCancel={() => {
          setIsFormOpen(false);
          setEditingDevice(null);
        }}
        isSubmitting={createMutation.isPending || updateMutation.isPending}
        isOpen={isFormOpen}
        defaultPollIntervalSeconds={DEFAULT_POLL_INTERVAL_SECONDS}
      />

      <DeleteConfirmationDialog
        isOpen={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setDeviceToDelete(null);
        }}
        onConfirm={confirmDelete}
        itemName={deviceToDelete?.name || ""}
        isDeleting={deleteMutation.isPending}
      />
    </div>
  );
};

/** One switch: what it is, whether it answers, and its ports. */
const SwitchDetail: React.FC<{ device: SNMPDevice; onBack: () => void; onEdit: () => void; onDelete: () => void }> = ({
  device,
  onBack,
  onEdit,
  onDelete,
}) => {
  const info = useSwitchInfo(device);
  const model = modelText(info.status);
  return (
    <div className="sv">
      <button className="gh-btn sw-back" onClick={onBack}>
        <ArrowLeftIcon /> Back
      </button>
      <section className="sv-hero">
        <div className="sv-hero-id">
          <div className="sv-hero-name">
            <StatusDot state={info.state} pulse={info.loading} />
            {device.name}
            <StatusPill info={info} />
          </div>
          <div className="sv-hero-meta">
            <span>
              <b>{`${device.host}:${device.port}`}</b>
            </span>
            <span>{`SNMPv${device.version}`}</span>
            <span>{`polled every ${device.pollIntervalSeconds}s`}</span>
            {info.status?.interfaceCount !== undefined && info.state === "up" && <span>{`${info.status.interfaceCount} ports`}</span>}
            {info.state === "up" && (
              <span>
                up <b>{uptimeText(info.status)}</b>
              </span>
            )}
            {model && <span title={info.status?.sysDescr}>{model.length > 70 ? `${model.slice(0, 70)}…` : model}</span>}
          </div>
        </div>
        <div className="sv-hero-actions">
          <button className="gh-btn" onClick={onEdit} title="Edit switch">
            <PencilIcon /> Edit
          </button>
          <button className="gh-btn gh-btn--danger" onClick={onDelete} title="Remove switch">
            <TrashIcon /> Remove
          </button>
        </div>
      </section>

      {info.label === "unreachable" && (
        <div className="sv-banner sv-banner--crit">
          <SignalSlashIcon />
          <span className="sv-banner-grow">
            The switch does not answer SNMP.
            {info.status?.error ? <span className="sv-mute">{` ${info.status.error}`}</span> : null}
          </span>
        </div>
      )}

      <SNMPDeviceDetailsTabs device={device} />
    </div>
  );
};
